import {
    Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges,
    ChangeDetectionStrategy, ChangeDetectorRef, Injector, ViewEncapsulation,
    ViewChild, ElementRef, AfterViewInit
} from '@angular/core';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import * as moment from 'moment';
import Litepicker from 'litepicker';
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';

declare const $: any;

const LP_PREV_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"></path></svg>';
const LP_NEXT_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"></path></svg>';

import { TimeTrackingService } from '../../services/time-tracking.service';
import { ProjectTimeEntryRow, ProjectTimeGroupBy, ProjectTimeFilter, AddTimeEntryInput, UpdateTimeEntryInput } from '../../models/time-tracking.models';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { DataService } from '@app/shared/common/auth/data.service';
import { guardGridFrameLoop } from '@app/main/shared/grid-frame-guard/grid-frame-guard';
import { TimeTrackingServiceProxy, TaskServiceProxy, ProjectTasksDto, ProjectMembers } from '@shared/service-proxies/service-proxies';
import { AppComponentBase } from '@shared/common/app-component-base';

@Component({
    selector: 'vf-project-time-tab',
    templateUrl: './project-time-tab.component.html',
    styleUrls: ['./project-time-tab.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class ProjectTimeTabComponent extends AppComponentBase implements OnInit, AfterViewInit, OnDestroy, OnChanges {

    @Input() projectId: number;
    @Input() projectName = '';
    @Input() isActive = false;

    @Output() requestDeleteEntry = new EventEmitter<any>();
    /** Fired after every loadEntries() completes — payload is the entry count.
     *  Used by the parent (view-project-modal) to drive the inactive-tab dot. */
    @Output() entriesCountChanged = new EventEmitter<number>();

    @ViewChild('rowMenuRef') rowMenuRef: ElementRef;
    @ViewChild('addEntryModalRef') addEntryModalRef: ElementRef;

    entries: ProjectTimeEntryRow[] = [];
    filteredEntries: ProjectTimeEntryRow[] = [];
    isLoading = false;
    isSaving = false;
    isDeleting = false;

    activeFilter: ProjectTimeFilter = 'allTime';
    groupBy: ProjectTimeGroupBy = 'date';
    searchText = '';

    showAddModal = false;
    editingEntryId: number | null = null;
    confirmDeleteId: number | null = null;

    // Add/edit form
    formDate = '';
    formTaskId: number | null = null;
    formTaskName = '';
    formUserId: number | null = null;
    formUserName = '';
    formStartTime = '';
    formEndTime = '';
    formDurationDisplay = '';

    // Task search
    taskSearchText = '';
    allTaskOptions: ProjectTasksDto[] = [];
    isSearchingTasks = false;
    showTaskDropdown = false;

    // Derived from allTaskOptions + taskSearchText so the menu auto-updates
    // when allTaskOptions arrives (loadTaskOptions is async, may resolve after
    // the user already opened the dropdown).
    get taskResults(): ProjectTasksDto[] {
        const all = this.allTaskOptions || [];
        const q = (this.taskSearchText || '').trim().toLowerCase();
        const list = q
            ? all.filter(t => t.title && t.title.toLowerCase().includes(q))
            : all;
        return list.slice(0, 20);
    }

    // Person search
    projectMembers: ProjectMembers[] = [];
    personSearchText = '';
    filteredMembers: ProjectMembers[] = [];
    showPersonDropdown = false;

    // Time dropdowns
    readonly timeOptions: string[] = Array.from({ length: 48 }, (_, i) => {
        const h = Math.floor(i / 2);
        const m = (i % 2) * 30;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    });
    showStartTimeDropdown = false;
    showEndTimeDropdown = false;

    maxDate = '';
    minDate = '';
    maxDateValue: Date = new Date();
    minDateValue: Date = new Date();
    formDateValue: Date | null = null;
    bsConfig = { containerClass: 'theme-default', showWeekNumbers: false, adaptivePosition: true, dateInputFormat: 'YYYY-MM-DD' };

    filterCounts: Record<ProjectTimeFilter, number> = {
        thisWeek: 0, thisMonth: 0, lastMonth: 0, allTime: 0
    };

    // ── ag-grid ─────────────────────────────────────────────────────────────
    private gridApi: GridApi;
    private columnApi: any; // ColumnApi for v29
    columnDefs: ColDef[] = [];
    defaultColDef: ColDef = {
        sortable: true,
        resizable: true,
        suppressMenu: true,
        suppressMovable: false
    };
    rowHeight = 44;
    // Match view-project-modal task grid pattern. Time tab adds a filter pills row
    // and a toolbar above the grid (compared to the task tab) but hides the
    // client/date/health row underneath the tabs nav.
    gridHeight = 'calc(100vh - 240px)';
    groupRowRendererParams: any = {
        suppressCount: true,
        suppressDoubleClickExpand: false,
        checkbox: false,
        innerRenderer: (params: any) => {
            if (!params.node || !params.node.group) { return ''; }
            const escape = (s: string) => String(s ?? '').replace(/[&<>"']/g, c =>
                ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
            const groupField = params.node.field;
            const itemCount = params.node.allChildrenCount || 0;
            const totalSecs = (params.node.allLeafChildren || []).reduce((acc: number, n: any) => acc + (n.data?.durationSeconds || 0), 0);
            const totalLabel = this.ttService.formatDuration(totalSecs);
            const label = groupField === 'date' ? this.formatDate(params.value) : (params.value || '');

            const clockSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color:#9ca3af;flex-shrink:0;"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>`;
            const userSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color:#9ca3af;flex-shrink:0;"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`;
            const listChecksSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color:#9ca3af;flex-shrink:0;"><path d="m3 17 2 2 4-4"/><path d="m3 7 2 2 4-4"/><path d="M13 6h8"/><path d="M13 12h8"/><path d="M13 18h8"/></svg>`;
            const icon = groupField === 'date' ? clockSvg : groupField === 'userName' ? userSvg : listChecksSvg;

            const countBadge = `<span style="display:inline-flex;align-items:center;padding:1px 6px;font-size:11px;line-height:1.4;border:1px solid #d1d5db;border-radius:4px;background:#fff;color:#4b5563;flex-shrink:0;">${itemCount}</span>`;

            return `<span class="d-flex align-items-center" style="gap:8px;">
                ${icon}
                <span style="color:#6b7280;font-size:13px;flex-shrink:0;">${escape(label)}</span>
                ${countBadge}
                <span style="color:#9ca3af;font-size:12px;flex-shrink:0;">· ${escape(totalLabel)}</span>
            </span>`;
        }
    };

    // Row context menu
    showRowMenu = false;
    rowMenuX = 0;
    rowMenuY = 0;
    rowMenuEntry: ProjectTimeEntryRow | null = null;

    private subs = new Subscription();
    private gridResizeObserver: ResizeObserver;
    private resizeListener: () => void;
    private datePicker: Litepicker;
    formUserAvatarUrl = '';
    isLoadingMembers = false;
    allEntries: ProjectTimeEntryRow[] = [];

    constructor(
        injector: Injector,
        public ttService: TimeTrackingService,
        private _proxy: TimeTrackingServiceProxy,
        private _taskProxy: TaskServiceProxy,
        private sessionService: AppSessionService,
        private dataService: DataService,
        private cdr: ChangeDetectorRef,
        private hostRef: ElementRef
    ) {
        super(injector);
    }

    ngAfterViewInit(): void {
        // Move the row context menu to <body> so it escapes any transformed
        // ancestor (Bootstrap modal) that breaks position: fixed alignment.
        if (this.rowMenuRef?.nativeElement) {
            document.body.appendChild(this.rowMenuRef.nativeElement);
        }

        // Same trick for the Add Entry modal — the view-project slider's
        // .modal-dialog has `transform: translate(0,0) !important`, which
        // creates a containing block for any `position: fixed` descendant.
        // Moving the wrapper to <body> lets the modal/backdrop cover the
        // entire viewport (including the left main menu).
        if (this.addEntryModalRef?.nativeElement) {
            document.body.appendChild(this.addEntryModalRef.nativeElement);
        }

        // Outside-click close for the row context menu. Capture phase so we
        // run BEFORE ag-grid's cellContextMenu — that lets a right-click on a
        // different row close the current menu first, then ag-grid's handler
        // synchronously re-opens it at the new row in the same JS tick (no
        // visual flicker, no need for the user to click outside first).
        document.addEventListener('click', this.onDocPointerEvent, true);
        document.addEventListener('contextmenu', this.onDocPointerEvent, true);

        // Size the grid from the actual modal-body so it scrolls internally
        // instead of overflowing the modal. Observe both modal-body and host
        // for size changes so the grid stays in sync.
        this.recomputeGridHeight();
        if (typeof ResizeObserver !== 'undefined') {
            this.gridResizeObserver = new ResizeObserver(() => this.recomputeGridHeight());
            const host = this.hostRef.nativeElement as HTMLElement;
            const modalBody = host.closest('.modal-body') as HTMLElement | null;
            if (modalBody) { this.gridResizeObserver.observe(modalBody); }
            this.gridResizeObserver.observe(host);
        }
        this.resizeListener = () => this.recomputeGridHeight();
        window.addEventListener('resize', this.resizeListener);
    }

    private recomputeGridHeight(): void {
        const host = this.hostRef.nativeElement as HTMLElement;
        const modalBody = host.closest('.modal-body') as HTMLElement | null;
        if (!modalBody) { return; }
        const filterH = (host.querySelector('.vf-pt-filter-bar') as HTMLElement)?.offsetHeight || 0;
        const toolbarH = (host.querySelector('.vf-pt-toolbar') as HTMLElement)?.offsetHeight || 0;
        if (filterH === 0 || toolbarH === 0) { return; } // not rendered yet
        const available = modalBody.clientHeight - filterH - toolbarH;
        if (available > 0 && this.gridHeight !== `${available}px`) {
            this.gridHeight = `${available}px`;
            this.cdr.markForCheck();
        }
    }

    ngOnInit(): void {
        this.maxDate = moment().format('YYYY-MM-DD');
        this.minDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
        this.maxDateValue = new Date();
        this.minDateValue = moment().subtract(30, 'days').toDate();
        this.formUserId = this.sessionService.userId as number;
        this.columnDefs = this.buildColumnDefs();
        this.subs.add(this.dataService.timerStopped.subscribe(() => {
            if (this.isActive) this.loadEntries();
        }));
        if (this.projectId && this.isActive) this.loadEntries();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes.isActive && this.isActive && this.projectId) ||
            (changes.projectId && this.isActive)) {
            this.loadEntries();
        }
    }

    loadEntries(): void {
        if (!this.projectId) return;
        this.isLoading = true;
        this.cdr.markForCheck();
        this.syncGridLoadingState();
        this._proxy.getProjectTimeEntries(this.projectId, 'allTime', null, null, null, null).pipe(
            map(res => (res.responseData || []).map(dto => this.mapDto(dto)))
        ).subscribe({
            next: rows => {
                this.allEntries = rows || [];
                this.calcFilterCounts();
                this.applyFilter();
                this.isLoading = false;
                this.cdr.markForCheck();
                this.syncGridLoadingState();
                this.entriesCountChanged.emit(this.allEntries.length);
                // After data renders the filter bar / toolbar / grid become measurable.
                setTimeout(() => this.recomputeGridHeight(), 0);
            },
            error: () => {
                this.allEntries = [];
                this.isLoading = false;
                this.cdr.markForCheck();
                this.syncGridLoadingState();
                this.entriesCountChanged.emit(0);
            }
        });
    }

    private syncGridLoadingState(): void {
        if (!this.gridApi) { return; }
        try {
            if (this.isLoading) {
                this.gridApi.showLoadingOverlay();
            } else {
                this.gridApi.hideOverlay();
            }
        } catch { /* ignore */ }
    }

    private mapDto(dto: any): ProjectTimeEntryRow {
        // Shift server UTC into the viewer's profile timezone so date grouping and
        // displayed start/end times follow the user's tz, not UTC or browser local.
        // Backend serializes TimeSpan.ToString() as "HH:MM:SS" (e.g. "-04:00:00")
        // which a stricter moment.utcOffset() pattern won't match — normalize to
        // ±HH:MM. Falls back to the company tz when the user's own tz is unset.
        const u = (this.sessionService && this.sessionService.user) as any;
        const rawOffset = u
            ? (((u.userTimeZoneOffset && String(u.userTimeZoneOffset).trim())
                 || (u.companyTimeZoneOffset && String(u.companyTimeZoneOffset).trim())) || '')
            : '';
        let userOffset: string | null = null;
        if (rawOffset) {
            const m = rawOffset.match(/^([+-]?)(\d{1,2}):(\d{2})(?::\d{2})?$/);
            userOffset = m ? `${m[1] || '+'}${m[2].padStart(2, '0')}:${m[3]}` : rawOffset;
        }
        const toUserMoment = (v: any): moment.Moment | null => {
            if (!v) { return null; }
            const s = String(v);
            const withTz = /Z$|[+-]\d{2}:\d{2}$/.test(s) ? s : s + 'Z';
            const m = moment.utc(withTz);
            if (!m.isValid()) { return null; }
            if (userOffset !== null && userOffset !== undefined && userOffset !== '') {
                m.utcOffset(userOffset);
            } else {
                m.local();
            }
            return m;
        };

        const start = toUserMoment(dto.startTime);
        const end   = toUserMoment(dto.endTime);
        // True elapsed seconds — server's DurationMinutes uses DATEDIFF MINUTE
        // and rounds sub-minute timers, so don't trust it for display/totals.
        let durationSeconds = (dto.durationMinutes || 0) * 60;
        if (start && end) {
            const ms = end.valueOf() - start.valueOf();
            if (!isNaN(ms) && ms >= 0) { durationSeconds = Math.floor(ms / 1000); }
        }
        return {
            id:              dto.id,
            taskId:          dto.taskId,
            taskName:        dto.taskName  || '',
            userId:          dto.userId,
            userName:        dto.userFullName || '',
            userInitials:    dto.userInitials || '',
            date:            start ? start.format('YYYY-MM-DD') : '',
            startTime:       start ? start.format('HH:mm')      : '',
            endTime:         end   ? end.format('HH:mm')        : '',
            durationMinutes: dto.durationMinutes || 0,
            durationSeconds,
        } as ProjectTimeEntryRow;
    }

    private calcFilterCounts(): void {
        const today = moment();
        const weekStart = today.clone().startOf('isoWeek').format('YYYY-MM-DD');
        const monthStart = today.clone().startOf('month').format('YYYY-MM-DD');
        const lastMonthStart = today.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        const lastMonthEnd = today.clone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');

        this.filterCounts.thisWeek = this.allEntries.filter(e => e.date >= weekStart).length;
        this.filterCounts.thisMonth = this.allEntries.filter(e => e.date >= monthStart).length;
        this.filterCounts.lastMonth = this.allEntries.filter(e => e.date >= lastMonthStart && e.date <= lastMonthEnd).length;
        this.filterCounts.allTime = this.allEntries.length;
    }

    setFilter(f: ProjectTimeFilter): void {
        this.activeFilter = f;
        this.applyFilter();
    }

    private applyFilter(): void {
        const today = moment();
        let entries = [...this.allEntries];

        switch (this.activeFilter) {
            case 'thisWeek': {
                const start = today.clone().startOf('isoWeek').format('YYYY-MM-DD');
                entries = entries.filter(e => e.date >= start);
                break;
            }
            case 'thisMonth': {
                const start = today.clone().startOf('month').format('YYYY-MM-DD');
                entries = entries.filter(e => e.date >= start);
                break;
            }
            case 'lastMonth': {
                const start = today.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
                const end = today.clone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
                entries = entries.filter(e => e.date >= start && e.date <= end);
                break;
            }
        }

        if (this.searchText) {
            const q = this.searchText.toLowerCase();
            entries = entries.filter(e =>
                e.taskName?.toLowerCase().includes(q) ||
                e.userName?.toLowerCase().includes(q) ||
                e.sectionName?.toLowerCase().includes(q)
            );
        }

        this.filteredEntries = entries;
        this.cdr.markForCheck();
    }

    onSearch(text: string): void {
        this.searchText = text;
        this.applyFilter();
    }

    setGroupBy(g: ProjectTimeGroupBy): void {
        this.groupBy = g;
        this.applyGridGrouping();
        // Refresh cells so the 3-dot moves to the correct (now-leftmost-visible) column
        if (this.gridApi) {
            this.gridApi.refreshCells({ columns: ['taskName', 'userName'], force: true });
        }
        this.cdr.markForCheck();
    }

    collapseAll(): void {
        if (this.gridApi) { this.gridApi.collapseAll(); }
    }

    expandAll(): void {
        if (this.gridApi) { this.gridApi.expandAll(); }
    }

    get totalTime(): string {
        // Sum the precise per-entry seconds, not server's rounded DurationMinutes,
        // so the toolbar total tracks the visible per-row durations exactly.
        const total = this.filteredEntries.reduce((acc, e) => acc + (e.durationSeconds || 0), 0);
        return this.ttService.formatDuration(total);
    }

    get isGrouped(): boolean {
        return this.groupBy !== 'none';
    }

    // ── ag-grid setup ───────────────────────────────────────────────────────
    onGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;
        this.columnApi = (event as any).columnApi;
        // ag-Grid runs chevron collapse and first-paint cell renderers on a frame queue that one throwing task
        // used to stop for good. See guardGridFrameLoop.
        guardGridFrameLoop(event.api);
        this.applyGridGrouping();
        this.syncGridLoadingState();
    }

    private buildColumnDefs(): ColDef[] {
        const escape = (s: string) => String(s ?? '').replace(/[&<>"']/g, c =>
            ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

        const actionBtn = `<button class="ag-action-btn flex-shrink-0 ml-2" data-action="row-menu" type="button" aria-label="Actions">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="5" r="1"></circle><circle cx="12" cy="12" r="1"></circle><circle cx="12" cy="19" r="1"></circle></svg>
        </button>`;

        const onActionClick = (p: any): boolean => {
            const target = p?.event?.target as HTMLElement;
            const menuBtn = target?.closest('[data-action="row-menu"]') as HTMLElement;
            if (menuBtn && p?.data) {
                p.event?.preventDefault?.();
                p.event?.stopPropagation?.();
                if (!this.canManageEntry(p.data as ProjectTimeEntryRow)) { return true; }
                const rect = menuBtn.getBoundingClientRect();
                this.openRowMenu(p.data, rect.right, rect.bottom);
                return true;
            }
            return false;
        };

        return [
            {
                headerName: 'Task',
                field: 'taskName',
                colId: 'taskName',
                flex: 2,
                minWidth: 200,
                cellRenderer: (p: ICellRendererParams) => {
                    if (!p.data) { return ''; }
                    const name = escape(p.data.taskName || '');
                    // 3-dot lives in Task cell unless we're grouping by task (then it moves to Person).
                    // Hide it entirely on rows the user can't edit/delete.
                    const showAction = this.groupBy !== 'task' && this.canManageEntry(p.data);
                    const action = showAction ? actionBtn : '';
                    return `<div class="d-flex align-items-center" style="gap: 8px; overflow: hidden;">
                        <span class="text-truncate text-gray-800 cursor-pointer f-size-14" data-action="open-task" title="${name}" style="flex:1; min-width:0;">${name}</span>
                        ${action}
                    </div>`;
                },
                onCellClicked: (p) => {
                    if (onActionClick(p)) { return; }
                    const target = (p.event as any)?.target as HTMLElement;
                    if (target?.closest('[data-action="open-task"]') && p.data) {
                        this.openTaskDetail(p.data.taskId);
                    }
                }
            },
            {
                headerName: 'Person',
                field: 'userName',
                colId: 'userName',
                flex: 1.5,
                minWidth: 140,
                cellRenderer: (p: ICellRendererParams) => {
                    if (!p.data) { return ''; }
                    const name = escape(p.data.userName || '');
                    const initials = escape(this.getInitials(p.data.userName));
                    const avatar = p.data.userAvatarUrl
                        ? `<img src="${escape(p.data.userAvatarUrl)}" class="vf-pt-avatar-img" alt="">`
                        : `<span class="vf-pt-avatar-initials">${initials}</span>`;
                    // When grouping by task, Task col is hidden so the 3-dot moves here —
                    // still gated by row-level permission.
                    const showAction = this.groupBy === 'task' && this.canManageEntry(p.data);
                    const action = showAction ? actionBtn : '';
                    return `<div class="d-flex align-items-center" style="gap: 8px; overflow: hidden;">
                        <span class="vf-pt-avatar flex-shrink-0">${avatar}</span>
                        <span class="text-truncate text-gray-700" style="flex:1; min-width:0;">${name}</span>
                        ${action}
                    </div>`;
                },
                onCellClicked: (p) => { onActionClick(p); }
            },
            {
                headerName: 'Section',
                field: 'sectionName',
                colId: 'sectionName',
                flex: 1,
                minWidth: 100,
                cellRenderer: (p: ICellRendererParams) => {
                    if (!p.data || !p.data.sectionName) { return '<span class="text-gray-400">—</span>'; }
                    const dot = p.data.sectionColor
                        ? `<span class="vf-pt-section-dot section-color ${escape(p.data.sectionColor)}"></span>`
                        : '';
                    return `<div class="d-flex align-items-center gap-8">${dot}<span class="text-truncate text-gray-600">${escape(p.data.sectionName)}</span></div>`;
                }
            },
            {
                headerName: 'Date',
                field: 'date',
                colId: 'date',
                width: 120,
                minWidth: 100,
                valueFormatter: (p) => p.value ? this.formatDate(p.value) : '',
                comparator: (a, b) => (a || '').localeCompare(b || '')
            },
            {
                headerName: 'Start – End',
                colId: 'timeRange',
                width: 180,
                minWidth: 150,
                sortable: false,
                valueGetter: (p) => p.data ? `${this.formatTime(p.data.startTime)} – ${this.formatTime(p.data.endTime)}` : ''
            },
            {
                headerName: 'Duration',
                field: 'durationSeconds',
                colId: 'durationMinutes',
                width: 110,
                minWidth: 90,
                cellClass: 'text-right',
                cellRenderer: (p: ICellRendererParams) =>
                    p.value !== null && p.value !== undefined
                        ? `<span class="font-weight-medium text-gray-800">${escape(this.ttService.formatDuration(p.value))}</span>`
                        : ''
            }
        ];
    }

    private applyGridGrouping(): void {
        if (!this.columnApi) { return; }
        const colMap: Record<string, string> = { date: 'date', person: 'userName', task: 'taskName' };
        const targetCol = colMap[this.groupBy];
        const groupableCols = ['date', 'userName', 'taskName'];
        const state = groupableCols.map(col => ({
            colId: col,
            rowGroup: col === targetCol,
            hide: col === targetCol
        }));
        this.columnApi.applyColumnState({ state, defaultState: { rowGroup: false, hide: false } });
    }

    // ── Litepicker (modal date) ─────────────────────────────────────────────
    getDatePickerElementId(): string {
        return 'vf-pt-modal-date-picker-' + (this.projectId || 'na');
    }

    private initDatePicker(): void {
        this.destroyDatePicker();
        setTimeout(() => {
            const el = document.getElementById(this.getDatePickerElementId()) as HTMLInputElement | null;
            if (!el) { return; }
            try {
                this.datePicker = new Litepicker({
                    element: el,
                    format: 'MMM D, YYYY',
                    autoApply: true,
                    firstDay: 7,
                    singleMode: true,
                    startDate: this.formDate ? moment(this.formDate).toDate() : '',
                    // Manual entries log time that already happened — block future dates.
                    maxDate: moment().endOf('day').toDate(),
                    buttonText: { previousMonth: LP_PREV_SVG, nextMonth: LP_NEXT_SVG, apply: 'Apply', cancel: 'Cancel', reset: '' },
                    setup: (picker) => {
                        picker.on('show', () => {
                            const ui = (picker as any).ui as HTMLElement | undefined;
                            if (!ui) { return; }
                            ui.classList.add('vf-compact-litepicker');
                            // Re-anchor below the visible pill (input is hidden inside it,
                            // so Litepicker's input-relative position lands a few px above
                            // the pill's bottom). Also adds a 4px gap.
                            const pill = el.closest('.vf-time-pill') as HTMLElement | null;
                            if (pill) {
                                const r = pill.getBoundingClientRect();
                                const sx = window.scrollX || (window as any).pageXOffset || 0;
                                const sy = window.scrollY || (window as any).pageYOffset || 0;
                                ui.style.top = (r.bottom + sy + 4) + 'px';
                                ui.style.left = (r.left + sx) + 'px';
                            }
                        });
                        picker.on('selected', (date1) => {
                            if (date1 && date1.dateInstance) {
                                this.formDate = moment(date1.dateInstance).format('YYYY-MM-DD');
                                this.formDateValue = date1.dateInstance;
                                this.cdr.markForCheck();
                            }
                            picker.hide();
                        });
                    }
                });
            } catch (e) { console.error('Litepicker init failed', e); }
        }, 100);
    }

    openDatePicker(): void {
        if (this.datePicker) { this.datePicker.show(); }
    }

    private destroyDatePicker(): void {
        if (this.datePicker) {
            try { this.datePicker.destroy(); } catch { /* ignore */ }
            this.datePicker = undefined;
        }
    }

    // ── Time period sections (Night / Morning / Afternoon / Evening) ────────
    getTimePeriodLabel(index: number): string | null {
        if (index === 0) { return 'Night'; }
        if (index === 12) { return 'Morning'; }
        if (index === 24) { return 'Afternoon'; }
        if (index === 36) { return 'Evening'; }
        return null;
    }

    // ── Permissions ─────────────────────────────────────────────────────────
    /**
     * Project managers (memberType === 1) can edit/delete any entry.
     * TODO: extend to also allow tenant/host admins once we agree on the
     *       admin signal — currently no app-wide flag is plumbed through here.
     */
    get isProjectManager(): boolean {
        const uid = this.sessionService.userId;
        if (!uid) { return false; }
        const me = (this.projectMembers || []).find(m => m.userId === uid);
        return !!me && (me as any).memberType === 1;
    }

    canManageEntry(entry: ProjectTimeEntryRow | null | undefined): boolean {
        if (!entry) { return false; }
        if (entry.userId === this.sessionService.userId) { return true; }
        return this.isProjectManager;
    }

    // ── Person picker (Bootstrap data-toggle dropdown) ──────────────────────
    get visibleMembers(): ProjectMembers[] {
        const all = (this.projectMembers || []).filter(m => m.userId !== this.formUserId);
        if (!this.personSearchText || !this.personSearchText.trim()) { return all; }
        const q = this.personSearchText.toLowerCase();
        return all.filter(m => m.memberName && m.memberName.toLowerCase().includes(q));
    }

    onOpenPersonDropdown(): void {
        this.personSearchText = '';
        setTimeout(() => {
            const el = document.querySelector('.vf-pt-modal-person-drop input[name="personSearchText"]') as HTMLInputElement;
            if (el) { el.focus(); }
        }, 100);
    }

    private closePersonDropdown(): void {
        try {
            $('.vf-pt-modal-person-drop').find('.dropdown-menu').removeClass('show');
            $('.vf-pt-modal-person-drop [data-toggle="dropdown"]').attr('aria-expanded', 'false');
            $('.vf-pt-modal-person-drop').removeClass('show');
        } catch { /* ignore */ }
    }

    // ── Row context menu ────────────────────────────────────────────────────
    openRowMenu(entry: ProjectTimeEntryRow, x: number, y: number): void {
        this.rowMenuEntry = entry;
        // Position menu just below-right of the button, then nudge left so it doesn't run off the screen
        const menuWidth = 140;
        this.rowMenuX = Math.max(8, x - menuWidth);
        this.rowMenuY = y + 4;
        this.showRowMenu = true;
        this.cdr.markForCheck();
    }

    onCellContextMenu(event: any): void {
        const mouseEvent = event?.event as MouseEvent;
        if (!mouseEvent) { return; }
        mouseEvent.preventDefault();
        // No menu on group header rows — they aren't a single time entry.
        if (event.node?.group) { return; }
        if (!event.data) { return; }
        // Permission gate: only the entry's owner (or a project manager) gets the menu.
        if (!this.canManageEntry(event.data as ProjectTimeEntryRow)) { return; }
        // openRowMenu treats (x,y) as the top-right anchor; pass clientX + menuWidth
        // so the menu's top-left lands at the cursor (standard context-menu feel).
        const menuWidth = 140;
        this.openRowMenu(event.data as ProjectTimeEntryRow, mouseEvent.clientX + menuWidth, mouseEvent.clientY - 4);
    }

    closeRowMenu(): void {
        this.showRowMenu = false;
        this.rowMenuEntry = null;
        this.cdr.markForCheck();
    }

    /**
     * Document capture-phase handler — closes the row context menu when the
     * user clicks (or right-clicks) anywhere outside it. Arrow function so
     * `this` is bound correctly when used as a DOM EventListener.
     */
    private onDocPointerEvent = (e: Event): void => {
        if (!this.showRowMenu) { return; }
        const target = e.target as Node | null;
        const menuEl = this.rowMenuRef?.nativeElement as HTMLElement | undefined;
        // Click/right-click inside the menu itself — leave it open so the
        // user can pick Edit/Delete.
        if (target && menuEl && menuEl.contains(target)) { return; }
        this.closeRowMenu();
        // Don't preventDefault — for a right-click on another row, ag-grid's
        // cellContextMenu still needs to fire (in the bubble phase that
        // follows this capture-phase handler) so the menu can re-open at
        // the new row's coordinates in the same tick.
    };

    onRowMenuEdit(): void {
        if (this.rowMenuEntry) { this.editEntry(this.rowMenuEntry); }
        this.closeRowMenu();
    }

    onRowMenuDelete(): void {
        if (this.rowMenuEntry) { this.requestDelete(this.rowMenuEntry); }
        this.closeRowMenu();
    }

    get formDateDisplay(): string {
        if (!this.formDate) return '';
        return moment(this.formDate).format('ddd, MMM D');
    }

    onDateChanged(date: Date): void {
        if (date) {
            this.formDate = moment(date).format('YYYY-MM-DD');
            this.formDateValue = date;
            this.cdr.markForCheck();
        }
    }

    // Add entry modal
    openAddModal(): void {
        this.editingEntryId = null;
        this.formDate = moment().format('YYYY-MM-DD');
        this.formDateValue = new Date();
        this.formTaskId = null;
        this.formTaskName = '';
        this.formUserId = this.sessionService.userId as number;
        const u = (this.sessionService as any).user;
        this.formUserName = u ? [u.name, u.surname].filter(Boolean).join(' ') : '';
        this.formUserAvatarUrl = u && u.profilePictureUrl ? u.profilePictureUrl : '';
        this.formStartTime = '';
        this.formEndTime = '';
        this.formDurationDisplay = '';
        this.taskSearchText = '';
        this.showTaskDropdown = false;
        this.allTaskOptions = [];
        this.personSearchText = '';
        this.filteredMembers = [];
        this.showPersonDropdown = false;
        this.showStartTimeDropdown = false;
        this.showEndTimeDropdown = false;
        this.showAddModal = true;
        this.updateBodyForCenterModalShow();
        this.cdr.markForCheck();
        this.loadTaskOptions();
        this.loadProjectMembers();
        this.initDatePicker();
    }

    private loadTaskOptions(): void {
        if (!this.projectId) return;
        this.isSearchingTasks = true;
        this.cdr.markForCheck();
        // Time-tracking only needs the raw project task list — no cycle filtering
        // (no dependency relationship being created here). Pass null for mode.
        this._taskProxy.getProjectTasksToLink(this.projectId, null, null).subscribe({
            next: res => {
                this.allTaskOptions = res.responseData || [];
                this.isSearchingTasks = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.allTaskOptions = [];
                this.isSearchingTasks = false;
                this.cdr.markForCheck();
            }
        });
    }

    private loadProjectMembers(): void {
        // Immediately seed from already-loaded entries so the dropdown isn't blank while API loads
        const seen = new Set<number>();
        const local: { userId: number; memberName: string }[] = [];
        if (this.formUserId) {
            seen.add(this.formUserId as number);
            local.push({ userId: this.formUserId as number, memberName: this.formUserName });
        }
        this.allEntries.forEach(e => {
            if (e.userId && !seen.has(e.userId)) {
                seen.add(e.userId);
                local.push({ userId: e.userId, memberName: e.userName });
            }
        });
        this.projectMembers = local as unknown as ProjectMembers[];
        this.cdr.markForCheck();

        if (!this.projectId) return;
        this._taskProxy.getProjectMembers(this.projectId, null, null, null).subscribe({
            next: res => {
                const api = (res.responseData || []).filter(m => !m.isDeleted && m.userId);
                if (api.length > 0) {
                    const apiIds = new Set(api.map(m => m.userId));
                    // Merge: keep API members + any local ones not in API
                    const extras = this.projectMembers.filter(m => !apiIds.has(m.userId));
                    this.projectMembers = [...api, ...extras];
                }
                this.cdr.markForCheck();
            },
            error: () => { /* keep seeded local list */ }
        });
    }

    onPersonFocus(): void {
        this.filteredMembers = this.projectMembers;
        this.showPersonDropdown = this.projectMembers.length > 0;
        this.cdr.markForCheck();
    }

    onPersonSearchInput(value: string): void {
        this.personSearchText = value;
        if (!value.trim()) {
            this.filteredMembers = this.projectMembers;
            this.showPersonDropdown = this.projectMembers.length > 0;
        } else {
            const q = value.toLowerCase();
            this.filteredMembers = this.projectMembers.filter(m => m.memberName?.toLowerCase().includes(q));
            this.showPersonDropdown = this.filteredMembers.length > 0;
        }
        this.cdr.markForCheck();
    }

    selectPerson(member: ProjectMembers): void {
        this.formUserId = member.userId ?? null;
        this.formUserName = member.memberName ?? '';
        this.formUserAvatarUrl = member.profilePictureUrl ?? '';
        this.personSearchText = '';
        this.filteredMembers = [];
        this.showPersonDropdown = false;
        this.closePersonDropdown();
        this.cdr.markForCheck();
    }

    clearPerson(): void {
        this.formUserId = null;
        this.formUserName = '';
        this.formUserAvatarUrl = '';
        this.personSearchText = '';
        this.filteredMembers = [];
        this.showPersonDropdown = false;
        this.cdr.markForCheck();
    }

    hidePersonDropdown(): void {
        setTimeout(() => {
            this.showPersonDropdown = false;
            this.cdr.markForCheck();
        }, 150);
    }

    onStartTimeFocus(_evt?: FocusEvent): void {
        this.showStartTimeDropdown = true;
        this.showEndTimeDropdown = false;
        this.cdr.markForCheck();
    }

    onEndTimeFocus(_evt?: FocusEvent): void {
        this.showEndTimeDropdown = true;
        this.showStartTimeDropdown = false;
        this.cdr.markForCheck();
    }

    hideStartTimeDropdown(): void {
        setTimeout(() => {
            this.showStartTimeDropdown = false;
            this.cdr.markForCheck();
        }, 150);
    }

    hideEndTimeDropdown(): void {
        setTimeout(() => {
            this.showEndTimeDropdown = false;
            this.cdr.markForCheck();
        }, 150);
    }

    selectStartTime(t: string): void {
        this.formStartTime = t;
        this.showStartTimeDropdown = false;
        this.onTimeChange();
    }

    selectEndTime(t: string): void {
        this.formEndTime = t;
        this.showEndTimeDropdown = false;
        this.onTimeChange();
    }

    onOpenTaskDropdown(): void {
        // Mirror onOpenPersonDropdown: reset search and focus the search input.
        // taskResults is a getter — it auto-tracks allTaskOptions and taskSearchText.
        this.taskSearchText = '';
        this.cdr.markForCheck();
        setTimeout(() => {
            const el = document.querySelector('.vf-pt-modal-task-drop input[name="taskSearchText"]') as HTMLInputElement;
            if (el) { el.focus(); }
        }, 100);
    }

    private closeTaskDropdown(): void {
        try {
            $('.vf-pt-modal-task-drop').find('.dropdown-menu').removeClass('show');
            $('.vf-pt-modal-task-drop [data-toggle="dropdown"]').attr('aria-expanded', 'false');
            $('.vf-pt-modal-task-drop').removeClass('show');
        } catch { /* ignore */ }
    }

    onTaskSearchInput(value: string): void {
        // Search only filters the menu list — does NOT clear formTaskId. Selection
        // is owned by the trigger pill (Select task / clear-X), separate from search.
        this.taskSearchText = value;
        this.cdr.markForCheck();
    }

    selectTask(task: ProjectTasksDto): void {
        this.formTaskId = task.id ?? null;
        this.formTaskName = task.title ?? '';
        this.taskSearchText = '';
        this.closeTaskDropdown();
        this.cdr.markForCheck();
    }

    clearTask(): void {
        this.formTaskId = null;
        this.formTaskName = '';
        this.taskSearchText = '';
        this.cdr.markForCheck();
    }

    editEntry(entry: ProjectTimeEntryRow): void {
        if (!this.canManageEntry(entry)) { return; }
        this.editingEntryId = entry.id;
        this.formDate = entry.date;
        this.formDateValue = entry.date ? moment(entry.date).toDate() : null;
        this.formTaskId = entry.taskId;
        this.formTaskName = entry.taskName;
        this.formUserId = entry.userId;
        this.formUserName = entry.userName || '';
        this.formUserAvatarUrl = entry.userAvatarUrl || '';
        this.formStartTime = entry.startTime;
        this.formEndTime = entry.endTime;
        this.taskSearchText = '';
        this.allTaskOptions = [];
        this.personSearchText = '';
        this.filteredMembers = [];
        this.showPersonDropdown = false;
        this.showTaskDropdown = false;
        this.showStartTimeDropdown = false;
        this.showEndTimeDropdown = false;
        this.showAddModal = true;
        this.updateBodyForCenterModalShow();
        this.onTimeChange();
        this.cdr.markForCheck();
        this.loadTaskOptions();
        this.loadProjectMembers();
        this.initDatePicker();
    }

    closeEntryModal(): void {
        this.showAddModal = false;
        this.destroyDatePicker();
        this.editingEntryId = null;
        this.updateBodyForCenterModalHide();
        this.cdr.markForCheck();
    }

    onTimeChange(): void {
        if (this.formStartTime && this.formEndTime) {
            const [sh, sm] = this.formStartTime.split(':').map(Number);
            const [eh, em] = this.formEndTime.split(':').map(Number);
            // formatDuration now takes seconds; HH:mm picker is minute-precision.
            const diffSecs = ((eh * 60 + em) - (sh * 60 + sm)) * 60;
            this.formDurationDisplay = diffSecs > 0 ? this.ttService.formatDuration(diffSecs) : '';
        } else {
            this.formDurationDisplay = '';
        }
        this.cdr.markForCheck();
    }

    get isFormValid(): boolean {
        if (!this.formDate || !this.formTaskId || !this.formUserId || !this.formStartTime || !this.formEndTime) return false;
        if (this.formDate > this.maxDate || this.formDate < this.minDate) return false;
        const [sh, sm] = this.formStartTime.split(':').map(Number);
        const [eh, em] = this.formEndTime.split(':').map(Number);
        const diff = (eh * 60 + em) - (sh * 60 + sm);
        if (diff < 1) return false;
        if (diff > 1440) return false;
        return true;
    }

    get formValidationError(): string {
        if (!this.formDate || !this.formStartTime || !this.formEndTime) return '';
        if (this.formDate > this.maxDate) return 'Time cannot be added for a future date.';
        if (this.formDate < this.minDate) return 'Time can only be added up to 30 days back.';
        if (!this.formStartTime || !this.formEndTime) return '';
        const [sh, sm] = this.formStartTime.split(':').map(Number);
        const [eh, em] = this.formEndTime.split(':').map(Number);
        const diff = (eh * 60 + em) - (sh * 60 + sm);
        if (diff <= 0) return 'End time must be after start time.';
        if (diff < 1) return 'Entry must be at least 1 minute.';
        if (diff > 1440) return 'Entry cannot be longer than 24 hours.';
        if (!this.formTaskId) return 'Select a task before saving.';
        return '';
    }

    /**
     * True if the form's date/time range overlaps another entry for the same
     * user (across the whole project, not just one task — a person can't be
     * billing two tasks for the same minute). The entry being edited is
     * excluded so saving an unchanged edit doesn't false-positive.
     */
    get hasOverlap(): boolean {
        if (!this.formDate || !this.formStartTime || !this.formEndTime || !this.formUserId) { return false; }
        return this.allEntries.some(e => {
            if (this.editingEntryId !== null && e.id === this.editingEntryId) { return false; }
            if (e.userId !== this.formUserId) { return false; }
            if (e.date !== this.formDate) { return false; }
            // HH:mm string compare works because lengths are equal and lexical ordering
            // matches numeric ordering for zero-padded times.
            return this.formStartTime < e.endTime && this.formEndTime > e.startTime;
        });
    }

    saveEntry(): void {
        if (!this.isFormValid || this.isSaving) return;
        this.isSaving = true;

        if (this.editingEntryId !== null) {
            const input: UpdateTimeEntryInput = {
                id: this.editingEntryId,
                taskId: this.formTaskId,
                userId: this.formUserId as number,
                date: this.formDate,
                startTime: this.formStartTime,
                endTime: this.formEndTime
            };
            this.ttService.updateTimeEntry(input).subscribe({
                next: () => {
                    this.isSaving = false;
                    this.editingEntryId = null;
                    this.showAddModal = false;
                    this.updateBodyForCenterModalHide();
                    this.destroyDatePicker();
                    this.loadEntries();
                },
                error: () => { this.isSaving = false; this.cdr.markForCheck(); }
            });
        } else {
            const input: AddTimeEntryInput = {
                taskId: this.formTaskId,
                userId: this.formUserId as number,
                date: this.formDate,
                startTime: this.formStartTime,
                endTime: this.formEndTime,
                projectId: this.projectId
            };
            this.ttService.addTimeEntry(input).subscribe({
                next: () => {
                    this.isSaving = false;
                    this.showAddModal = false;
                    this.updateBodyForCenterModalHide();
                    this.destroyDatePicker();
                    this.loadEntries();
                },
                error: () => { this.isSaving = false; this.cdr.markForCheck(); }
            });
        }
    }

    /**
     * Emits the entry-to-delete to the parent (view-project-modal). The parent
     * shows the centered <vf-delete-popup> and calls performDeleteEntry() on
     * confirm. Done this way so the popup escapes view-project-modal's nested
     * modal-stack styling, where it would otherwise look wrong.
     */
    requestDelete(entry: ProjectTimeEntryRow): void {
        if (!this.canManageEntry(entry)) { return; }
        this.requestDeleteEntry.emit({
            id: entry.id,
            title: entry.taskName ? `Time entry on "${entry.taskName}"` : 'Time entry'
        });
    }

    /** Public — invoked by the parent after user confirms in vf-delete-popup. */
    performDeleteEntry(obj: any): void {
        if (!obj || !obj.id || this.isDeleting) { return; }
        this.isDeleting = true;
        this.cdr.markForCheck();
        this.ttService.deleteTimeEntry(obj.id).subscribe({
            next: () => {
                this.isDeleting = false;
                this.loadEntries();
            },
            error: () => { this.isDeleting = false; this.cdr.markForCheck(); }
        });
    }

    exportExcel(): void {
        // Export pulls from filteredEntries — respects filter/search and ignores
        // ag-grid's collapsed state (collapse is purely visual; collapsed rows
        // are still in filteredEntries).
        if (this.filteredEntries.length === 0) { return; }
        const rows: string[][] = [['Task', 'Person', 'Section', 'Date', 'Start', 'End', 'Duration', 'Hours']];
        let totalSeconds = 0;
        this.filteredEntries.forEach(e => {
            const secs = e.durationSeconds || 0;
            totalSeconds += secs;
            rows.push([
                e.taskName || '',
                e.userName || '',
                e.sectionName || '',
                e.date || '',
                this.formatTime(e.startTime),
                this.formatTime(e.endTime),
                this.ttService.formatDuration(secs),
                // 4 decimal places so sub-minute entries don't collapse to ~0
                // (a 31-second entry is 0.0086h — toFixed(2) would round to 0.01).
                (secs / 3600).toFixed(4)
            ]);
        });
        // Total row — derived from raw seconds so it matches the on-screen total
        // exactly (summing pre-rounded Hours decimals would drift).
        rows.push(['', '', '', '', '', 'Total', this.ttService.formatDuration(totalSeconds), (totalSeconds / 3600).toFixed(4)]);

        const escape = (c: string) => `"${String(c ?? '').replace(/"/g, '""')}"`;
        const csv = rows.map(r => r.map(escape).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const filterLabel = this.activeFilter === 'thisWeek' ? 'ThisWeek' : this.activeFilter === 'thisMonth' ? 'ThisMonth' : this.activeFilter === 'lastMonth' ? 'LastMonth' : 'AllTime';
        link.download = `${this.projectName || 'Project'}_Time_${filterLabel}.csv`;
        link.click();
    }

    formatTime(t: string): string {
        if (!t) return '';
        const [h, m] = t.split(':').map(Number);
        const period = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return `${h12}:${String(m).padStart(2, '0')} ${period}`;
    }

    formatDate(d: string): string {
        if (!d) return '';
        const m = moment(d);
        return m.year() === moment().year() ? m.format('MMM D') : m.format('MMM D, YYYY');
    }

    getInitials(name: string): string {
        if (!name) return '?';
        return name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
    }

    openTaskDetail(taskId: number): void {
        // edit-task-modal.show(data) reads data[0].id / data[0].projectId, so we must
        // emit a [taskShape, ...] array (matches the rest of the app), not a bare object.
        const task = { id: taskId, projectId: this.projectId };
        this.dataService.openEditTaskModal.next([task, false, false]);
    }

    ngOnDestroy(): void {
        // Detach the document capture-phase listeners that drive outside-click
        // close for the row context menu.
        document.removeEventListener('click', this.onDocPointerEvent, true);
        document.removeEventListener('contextmenu', this.onDocPointerEvent, true);
        // Clean up the body-mounted row context menu
        const menuEl = this.rowMenuRef?.nativeElement;
        if (menuEl && menuEl.parentElement === document.body) {
            document.body.removeChild(menuEl);
        }
        // Clean up the body-mounted Add Entry modal wrapper
        const addEl = this.addEntryModalRef?.nativeElement;
        if (addEl && addEl.parentElement === document.body) {
            document.body.removeChild(addEl);
        }
        this.destroyDatePicker();
        if (this.gridResizeObserver) {
            try { this.gridResizeObserver.disconnect(); } catch { /* ignore */ }
        }
        if (this.resizeListener) {
            window.removeEventListener('resize', this.resizeListener);
        }
        this.subs.unsubscribe();
    }
}
